// 2026-11-02 (James's idea): the report button on every screen. One tap takes a picture of the screen as it is, then
// asks what went wrong (or the idea) and sends both to support: the same row and email as the Director's
// report_problem / send_feedback, plus the screenshot attached (POST /api/feedback).
import { call } from "./api";

export type ReportKind = "problem" | "feedback";

/** The screenshot's longest side and JPEG quality: readable, and a few hundred KB (the api takes up to 4 MB). */
export const SHOT_MAX_SIDE = 1600;
export const SHOT_QUALITY = 0.72;

/** How much to scale the page so its longest side fits SHOT_MAX_SIDE (never up). */
export function shotScale(width: number, height: number, max = SHOT_MAX_SIDE): number {
  const side = Math.max(1, width, height);
  return Math.min(1, max / side);
}

/** Elements marked data-no-shot (the report button and its sheet) stay out of the picture. */
export const keepInShot = (node: Node): boolean => !(node instanceof HTMLElement && node.dataset.noShot !== undefined);

/** A JPEG data URL of what's on screen now, or null if the browser can't draw it. Never throws. */
export async function captureScreen(): Promise<string | null> {
  try {
    const { domToJpeg } = await import("modern-screenshot");
    const el = document.documentElement;
    const width = window.innerWidth;
    const height = window.innerHeight;
    return await domToJpeg(el, {
      width,
      height,
      scale: shotScale(width, height) * Math.min(2, window.devicePixelRatio || 1),
      quality: SHOT_QUALITY,
      backgroundColor: getComputedStyle(document.body).backgroundColor || "#0b0b0f",
      filter: keepInShot,
      // what's scrolled into view, not the top of the page
      style: { transform: `translate(${-window.scrollX}px, ${-window.scrollY}px)` },
      timeout: 8000,
    });
  } catch {
    return null;
  }
}

export type ReportPayload = { kind: ReportKind; text: string; path: string; projectId: string | null; sentryEventIds: string[]; screenshot: string | null };

/** What the button sends (trimmed; no screenshot when the user took it off). */
export function reportBody(p: ReportPayload) {
  return {
    kind: p.kind,
    text: p.text.trim(),
    path: p.path.slice(0, 300),
    ...(p.projectId ? { projectId: p.projectId } : {}),
    ...(p.sentryEventIds.length ? { sentryEventIds: p.sentryEventIds.slice(-5) } : {}),
    ...(p.screenshot ? { screenshot: p.screenshot } : {}),
  };
}

export async function sendReport(p: ReportPayload): Promise<{ id: string; duplicate: boolean }> {
  const r = await call<{ id?: string; duplicate?: boolean }>("POST", "feedback", reportBody(p));
  return { id: String(r?.id ?? ""), duplicate: !!r?.duplicate };
}
